import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CloseUniversal } from '@duxton-web/icons';
import {
  Button,
  Modal,
  notification,
  Tabs,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { AccessControlWrapper } from '@/common/components/AccessControlWrapper';
import { WarningIcon } from '@/common/Icons';
import { variableSettingsEvents } from '@/common/Scribe/EventsList';
import { HTTP_STATUS, VariableType } from '@/constants';
import { useIsSyncedFromGrab } from '@/features/tenant-sync';
import {
  ProductEntityAction,
  VariableSettingsAction,
} from '@/features/variable/constants';
import {
  useVariableContext,
  useVariableDispatchContext,
} from '@/features/variable/contexts/variable-context';
import Archive from '@/features/variable/settings/archive';
import Canary, {
  isCanaryEligible,
  mapVariableCanaryToConfig,
  validateCanaryConfig,
} from '@/features/variable/settings/canary';
import {
  ENABLE_CANARY_ON_OPEN_STATE,
  enableCanaryForReview,
} from '@/features/variable/settings/canary/enablement-utils';
import ContextualRulesExceptionModal from '@/features/variable/settings/contextual-rules-exception-modal';
import VariableDetail from '@/features/variable/settings/detail';
import GetVariableValue from '@/features/variable/settings/get-variable-value';
import Stats from '@/features/variable/settings/stats';
import Validation from '@/features/variable/settings/validation';
import {
  setVariable,
  variableDetailUpdate,
} from '@/features/variable/store/actions/variable/action-creators';
import {
  isContextualRulesValid,
  isRolloutPage,
  needsContextualRulesException,
  supportsValidation,
} from '@/features/variable/utils';
import {
  useGetCanaryPilotTechFamilies,
  useGetVariable,
  useUpdateVariable,
} from '@/hooks';
import Service from '@/lib/App';
import { ContextualRule, VariableInfo } from '@/lib/PlannerDTOs';
import { FormatErrorMessage } from '@/services/Format';
import {
  buildAccessTargetFromVariable,
  UserAction,
} from '@/utils/accessControl';
import { isEqual } from '@/utils/object';
import { sendScribeEvent } from '@/utils/scribe';
import { formatHasAccessErrorGeneric } from '@/utils/url';

import '@/features/variable/settings/edit-variable-modal/index.css';

import DeleteVariable from '../delete-variable';

function removeNullUndefinedFromObject(
  obj: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, v]) => v !== null && v !== undefined)
      .map(([k, v]) => [
        k,
        Array.isArray(v)
          ? (v as unknown[]).filter(
              (item) => item !== null && item !== undefined,
            )
          : typeof v === 'object' && v !== null
            ? removeNullUndefinedFromObject(v as Record<string, unknown>)
            : v,
      ])
      .filter(([, v]) => !Array.isArray(v) || (v as unknown[]).length > 0),
  );
}

function sanitizeVariable(variable: VariableInfo | null): VariableInfo | null {
  if (!variable) return null;

  const sanitized = { ...variable };

  if (sanitized.metadata) {
    const metadata = { ...sanitized.metadata };

    if (metadata.jsonSchema) {
      try {
        const parsed = JSON.parse(metadata.jsonSchema);
        const cleaned = removeNullUndefinedFromObject(parsed);
        metadata.jsonSchema = JSON.stringify(cleaned);
      } catch {
        // leave as-is if not valid JSON
      }
    }

    if (metadata.contextualRules) {
      metadata.contextualRules = metadata.contextualRules
        .map((rule) => {
          const cleaned: ContextualRule = {
            contextualRulesConditions: rule.contextualRulesConditions,
          };
          if (rule.minimumLength != null)
            cleaned.minimumLength = rule.minimumLength;
          if (rule.maximumLength != null)
            cleaned.maximumLength = rule.maximumLength;
          if (rule.minimumValue != null)
            cleaned.minimumValue = rule.minimumValue;
          if (rule.maximumValue != null)
            cleaned.maximumValue = rule.maximumValue;
          if (rule.allowedValues != null) {
            const filteredAllowedValues = rule.allowedValues.filter(
              (v) => v != null,
            ) as ContextualRule['allowedValues'];
            if (filteredAllowedValues?.length)
              cleaned.allowedValues = filteredAllowedValues;
          }
          if (rule.jsonSchema != null) cleaned.jsonSchema = rule.jsonSchema;
          return cleaned;
        })
        .filter((rule) => {
          return (
            rule.allowedValues != null ||
            rule.minimumLength != null ||
            rule.maximumLength != null ||
            rule.minimumValue != null ||
            rule.maximumValue != null ||
            rule.jsonSchema != null
          );
        });
    }

    sanitized.metadata = metadata;
  }

  return sanitized;
}

const SecondaryActions = ({
  activeTab,
  onDeleteAndArchiveClick = () => {},
  onDeleteAndArchiveCancel = () => {},
  onArchiveOk = () => {},
  onDeleteClick = () => {},
  onDeleteOk = () => {},
  onDeleteCancel = () => {},
}: {
  activeTab: VariableSettingsAction;
  onDeleteAndArchiveClick?: () => void;
  onDeleteAndArchiveCancel?: () => void;
  onArchiveOk?: () => void;
  onDeleteClick?: () => void;
  onDeleteOk?: () => void;
  onDeleteCancel?: () => void;
}) => {
  const { variable } = useVariableContext();

  const { type } = variable || {};

  switch (activeTab) {
    case VariableSettingsAction.EDIT:
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Archive
            onDeleteAndArchiveClick={onDeleteAndArchiveClick}
            onDeleteAndArchiveCancel={onDeleteAndArchiveCancel}
            onArchiveOk={onArchiveOk}
          />
          <DeleteVariable
            onDeleteClick={onDeleteClick}
            onDeleteOk={onDeleteOk}
            onDeleteCancel={onDeleteCancel}
          />
        </div>
      );
    case VariableSettingsAction.TEST:
    case VariableSettingsAction.STATS:
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>{null}</div>
      );
    case VariableSettingsAction.VALIDATE:
      if (!supportsValidation(type as VariableType))
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>{null}</div>
        );

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Archive
            onDeleteAndArchiveClick={onDeleteAndArchiveClick}
            onDeleteAndArchiveCancel={onDeleteAndArchiveCancel}
            onArchiveOk={onArchiveOk}
          />
          <DeleteVariable
            onDeleteClick={onDeleteClick}
            onDeleteOk={onDeleteOk}
            onDeleteCancel={onDeleteCancel}
          />
        </div>
      );
    case VariableSettingsAction.CANARY:
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Archive
            onDeleteAndArchiveClick={onDeleteAndArchiveClick}
            onDeleteAndArchiveCancel={onDeleteAndArchiveCancel}
            onArchiveOk={onArchiveOk}
          />
          <DeleteVariable
            onDeleteClick={onDeleteClick}
            onDeleteOk={onDeleteOk}
            onDeleteCancel={onDeleteCancel}
          />
        </div>
      );
  }
};

const PrimaryActions = ({
  activeTab,
  onCancel,
}: {
  activeTab: VariableSettingsAction;
  onCancel: () => void;
}) => {
  const {
    variable,
    currentVariable,
    disabled,
    variableIsValid,
    jsonSchemaIsValid,
  } = useVariableContext();
  const variableDispatch = useVariableDispatchContext();
  const isSyncedFromGrab = useIsSyncedFromGrab(variable);
  const [showExceptionModal, setShowExceptionModal] = useState(false);
  const [isValidatingMetrics, setIsValidatingMetrics] = useState(false);

  const { data: canaryFeatureConfig } = useGetCanaryPilotTechFamilies();
  const isCanaryPilotEnrolledForVariable = useMemo(() => {
    if (!canaryFeatureConfig) return false;
    if (canaryFeatureConfig.usePilotTechFamilies === false) return true;
    if (!variable?.techFamily) return false;
    return canaryFeatureConfig.techFamilies.includes(variable.techFamily);
  }, [canaryFeatureConfig, variable?.techFamily]);

  const isCanaryValid = useMemo(() => {
    // Only gate when pilot is enrolled AND variable is canary-eligible
    // (assignBy must be pax / mex / dax).
    if (!isCanaryPilotEnrolledForVariable || !isCanaryEligible(variable))
      return true;
    const cc = variable?.metadata?.canaryConfig;
    // Existing variable that has never had canary configured — allow save as-is.
    if (!cc) return true;
    if (!cc.enabled) {
      // A reason is required whenever canary is disabled (edit or create).
      if (!cc.disabledReason) return false;
      // When 'other' is selected, the free-text note must be non-empty.
      if (cc.disabledReason === 'other' && !cc.disabledReasonNote?.trim())
        return false;
      return true;
    }
    return validateCanaryConfig(mapVariableCanaryToConfig(variable)).isValid;
  }, [
    isCanaryPilotEnrolledForVariable,
    variable?.assignBy,
    variable?.metadata?.canaryConfig,
  ]);

  const { mutate } = useUpdateVariable({
    onSuccess: (savedVariable: any) => {
      notification.success({
        message: `Variable updated`,
        description: `Variable ${variable?.name} was successfully updated.`,
      });

      sendScribeEvent(variableSettingsEvents.saveVariable.name, {
        STATE_NAME: variableSettingsEvents.saveVariable.state,
        ...variable,
        error: '',
      });

      onCancel();

      // Update the Redux store with the server's response so the rollout page
      // picks up the latest variable data (e.g. canary enabled state) without
      // a full page reload.
      if (savedVariable) {
        variableDispatch(setVariable(savedVariable, false, null));
      }
    },
    onError: (err: any) => {
      let description = (
        <span>
          {`Variable was not ${
            variable?.name ? 'updated' : 'saved'
          }, due to: ${FormatErrorMessage(err)}`}
        </span>
      );

      if (err?.response?.status === HTTP_STATUS.FORBIDDEN) {
        description = formatHasAccessErrorGeneric(err);
      }

      notification.error({
        description,
        message: 'Updating variable failed',
      });

      sendScribeEvent(variableSettingsEvents.saveVariable.name, {
        STATE_NAME: variableSettingsEvents.saveVariable.state,
        ...variable,
        error: err,
      });
    },
  });

  const doSave = async (exceptionReason?: string) => {
    // Auto-validate all canary metrics and monitors in parallel before saving.
    if (isCanaryPilotEnrolledForVariable && isCanaryEligible(variable)) {
      const cc = variable?.metadata?.canaryConfig;
      // Split mixed metrics[] into query rows and monitor rows (new merged format).
      const rawMetrics: any[] = cc?.metrics ?? [];
      const queryRows = rawMetrics.filter((m) => !m.type || m.type === 'query');
      const monitorRowsInMetrics = rawMetrics.filter(
        (m) => m.type === 'monitor' && !!m.monitorUrl,
      );
      const allMonitorRows = [
        ...(cc?.monitors ?? []).filter((mon) => !!(mon as any).monitorUrl),
        ...monitorRowsInMetrics,
      ];
      const hasMetrics = queryRows.length > 0;
      const hasMonitors = allMonitorRows.length > 0;
      if (cc?.enabled && (hasMetrics || hasMonitors)) {
        setIsValidatingMetrics(true);
        const varName = variable?.name ?? '';

        const [metricResults, monitorResults] = await Promise.all([
          Promise.all(
            queryRows.map((m) =>
              Service.planner!.validateCanaryQuery({
                query: m.query,
                variable_name: varName,
              }),
            ),
          ),
          Promise.all(
            allMonitorRows.map((mon) =>
              Service.planner!.validateCanaryQuery({
                type: 'monitor',
                variable_name: varName,
                monitor_url: mon.monitorUrl ?? mon.monitorURL,
              }),
            ),
          ),
        ]);

        setIsValidatingMetrics(false);

        const metricFailures = metricResults.some((r) => !r.valid);
        const monitorFailures = monitorResults.some((r) => !r.valid);

        if (metricFailures || monitorFailures) {
          // Re-map query results back onto rawMetrics (skip monitor-type rows).
          let queryResultIdx = 0;
          const updatedMetrics = rawMetrics.map((m) => {
            if (m.type === 'monitor') return m;
            const r = metricResults[queryResultIdx++];
            return {
              ...m,
              queryValidated: r?.valid ? (m as any).queryValidated : false,
            };
          });

          // Re-index monitor results against only cc.monitors that have a URL.
          let monitorResultIdx = 0;
          const updatedMonitors = (cc?.monitors ?? []).map((mon) => {
            if (!(mon as any).monitorUrl) return mon;
            const r = monitorResults[monitorResultIdx++];
            return {
              ...mon,
              monitorValidated: r?.valid
                ? (mon as any).monitorValidated
                : false,
            };
          });

          variableDispatch(
            variableDetailUpdate(
              {
                ...variable,
                metadata: {
                  ...variable?.metadata,
                  canaryConfig: {
                    ...cc,
                    metrics: updatedMetrics,
                    monitors: updatedMonitors,
                  },
                },
              } as VariableInfo,
              variableIsValid,
            ),
          );
          return;
        }
      }
    }

    const sanitized = sanitizeVariable(variable) as VariableInfo;
    const variableToSave =
      exceptionReason !== undefined
        ? ({
            ...sanitized,
            complexity: {
              ...sanitized?.complexity,
              guardrails: {
                ...sanitized?.complexity?.guardrails,
                validation: {
                  ...sanitized?.complexity?.guardrails?.['validation'],
                  exceptionReason,
                },
              },
            },
          } as VariableInfo)
        : sanitized;
    mutate(variableToSave);
    variableDispatch(setVariable(null, false, null));
    onCancel();
  };

  const handleSaveClick = () => {
    if (needsContextualRulesException(variable)) {
      setShowExceptionModal(true);
    } else {
      doSave();
    }
  };

  switch (activeTab) {
    case VariableSettingsAction.EDIT:
    case VariableSettingsAction.VALIDATE:
    case VariableSettingsAction.CANARY:
      return (
        <>
          <ContextualRulesExceptionModal
            open={showExceptionModal}
            actionLabel={!variable?.name ? 'Create' : 'Save'}
            onConfirm={(reason) => {
              setShowExceptionModal(false);
              doSave(reason);
            }}
            onCancel={() => setShowExceptionModal(false)}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Button
              type="default"
              size="small"
              onClick={() => {
                variableDispatch(setVariable(null, false, null));

                onCancel();
              }}
            >
              Cancel
            </Button>
            <AccessControlWrapper
              action={
                currentVariable?.name
                  ? UserAction.EDIT_CURRENT_VARIABLE
                  : UserAction.CREATE_VARIABLE_USING_CURRENT_TF
              }
              techFamilies={[currentVariable?.techFamily]}
              accessTargets={[
                buildAccessTargetFromVariable(currentVariable ?? variable),
              ]}
            >
              {(hasAccess) => (
                <Button
                  type="primary"
                  size="small"
                  onClick={handleSaveClick}
                  loading={isValidatingMetrics}
                  disabled={
                    !hasAccess ||
                    disabled ||
                    isSyncedFromGrab ||
                    !variableIsValid ||
                    !jsonSchemaIsValid ||
                    !isContextualRulesValid(variable) ||
                    !isCanaryValid ||
                    (isEqual(currentVariable, variable) &&
                      isEqual(variable, currentVariable))
                  }
                >
                  {!variable?.name ? 'Create' : 'Save'}
                </Button>
              )}
            </AccessControlWrapper>
          </div>
        </>
      );
    default:
      return null;
  }
};

interface FooterProps {
  activeTab: VariableSettingsAction;
  onCancel?: () => void;
  onDeleteAndArchiveClick?: () => void;
  onDeleteAndArchiveCancel?: () => void;
  onArchiveOk?: () => void;
  onDeleteClick?: () => void;
  onDeleteOk?: () => void;
  onDeleteCancel?: () => void;
}

const Footer = ({
  activeTab,
  onCancel = () => {},
  onDeleteAndArchiveClick = () => {},
  onDeleteAndArchiveCancel = () => {},
  onArchiveOk = () => {},
  onDeleteClick = () => {},
  onDeleteOk = () => {},
  onDeleteCancel = () => {},
}: FooterProps) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <SecondaryActions
        activeTab={activeTab}
        onDeleteAndArchiveClick={onDeleteAndArchiveClick}
        onDeleteAndArchiveCancel={onDeleteAndArchiveCancel}
        onArchiveOk={onArchiveOk}
        onDeleteClick={onDeleteClick}
        onDeleteOk={onDeleteOk}
        onDeleteCancel={onDeleteCancel}
      />
      <PrimaryActions activeTab={activeTab} onCancel={onCancel} />
    </div>
  );
};

const DetailTab = () => {
  const { variableIsValid } = useVariableContext();

  if (variableIsValid) return 'Details';

  return (
    <Tooltip title="Some required fields are empty or invalid.">
      <div
        data-testid="details-warning-icon"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Typography.Text>Details</Typography.Text>
        <WarningIcon />
      </div>
    </Tooltip>
  );
};

const ValidationTab = () => {
  const { jsonSchemaIsValid } = useVariableContext();

  if (jsonSchemaIsValid) return 'Validate';

  return (
    <Tooltip title="Invalid validation.">
      <div
        data-testid="validate-warning-icon"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Typography.Text>Validate</Typography.Text>
        <WarningIcon />
      </div>
    </Tooltip>
  );
};

const EditVariableModalTitle = ({ onCancel }: { onCancel: () => void }) => {
  const variableDispatch = useVariableDispatchContext();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography.Title level={3}>Variable Settings</Typography.Title>
      <Button
        type="default"
        shape="circle"
        ghost
        size="small"
        onClick={() => {
          variableDispatch(setVariable(null, false, null));

          onCancel();
        }}
        icon={<CloseUniversal />}
      />
    </div>
  );
};

interface EditVariableModalProps {
  variableName: string;
  onArchiveOk?: () => void;
  onDeleteOk?: () => void;
}

const EditVariableModal = ({
  variableName,
  onArchiveOk = () => {},
  onDeleteOk = () => {},
}: EditVariableModalProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{
    variableName: string;
    action: string;
  }>();
  const variableContext = useVariableContext();
  const { variable } = variableContext;
  const { variableName: paramVariableName, action } = params;
  const { pathname } = location;

  const variableDispatch = useVariableDispatchContext();
  const canaryIntentAppliedRef = useRef(false);

  const [activeTab, setActiveTab] = useState<VariableSettingsAction>(
    VariableSettingsAction.EDIT,
  );
  const [isDisplayNone, setIsDisplayNone] = useState<boolean>(false);

  const isOpen = useMemo(() => {
    return (
      [
        VariableSettingsAction.EDIT,
        VariableSettingsAction.TEST,
        VariableSettingsAction.STATS,
        VariableSettingsAction.VALIDATE,
        VariableSettingsAction.CANARY,
      ].includes(action as VariableSettingsAction) &&
      variableName === paramVariableName
    );
  }, [action, variableName, paramVariableName]);

  const showDetailTab = useMemo(() => {
    return (
      [
        VariableSettingsAction.EDIT,
        VariableSettingsAction.EDIT_DELETE_AND_ARCHIVE,
        ProductEntityAction.MANAGE_ENTITIES,
        ProductEntityAction.CREATE_ENTITY,
        ProductEntityAction.DELETE_ENTITY,
      ].includes(action as VariableSettingsAction) &&
      variableName === paramVariableName
    );
  }, [action, variableName, paramVariableName]);

  const showValidateTab = useMemo(() => {
    return (
      [
        VariableSettingsAction.VALIDATE,
        VariableSettingsAction.VALIDATE_DELETE_AND_ARCHIVE,
      ].includes(action as VariableSettingsAction) &&
      variableName === paramVariableName
    );
  }, [action, variableName, paramVariableName]);

  const { data, isLoading, error } = useGetVariable(variableName, {
    enabled: isOpen && variable?.name !== paramVariableName,
  });

  const { data: canaryConfig } = useGetCanaryPilotTechFamilies({
    enabled: isOpen,
  });

  const isCanaryPilotEnrolled = useMemo(() => {
    if (!canaryConfig) return false;
    if (canaryConfig.usePilotTechFamilies === false) return true;
    if (!variable?.techFamily) return false;
    return canaryConfig.techFamilies.includes(variable.techFamily);
  }, [canaryConfig, variable?.techFamily]);

  useEffect(() => {
    if (data) {
      variableDispatch(setVariable(data, isLoading, error));
    }
  }, [data, isLoading, error]);

  useEffect(() => {
    setActiveTab(
      showDetailTab
        ? VariableSettingsAction.EDIT
        : showValidateTab
          ? VariableSettingsAction.VALIDATE
          : (action as VariableSettingsAction),
    );
  }, [action]);

  useEffect(() => {
    const navigationState = (location.state ?? {}) as Record<string, unknown>;
    const shouldEnableCanary =
      action === VariableSettingsAction.CANARY &&
      navigationState[ENABLE_CANARY_ON_OPEN_STATE] === true;

    if (!shouldEnableCanary) {
      canaryIntentAppliedRef.current = false;
      return;
    }

    if (!variable || canaryIntentAppliedRef.current) return;

    canaryIntentAppliedRef.current = true;
    variableDispatch(
      variableDetailUpdate(
        enableCanaryForReview(variable),
        variableContext.variableIsValid,
      ),
    );

    const {
      [ENABLE_CANARY_ON_OPEN_STATE]: _enableCanaryOnOpen,
      ...remainingState
    } = navigationState;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: remainingState,
      },
    );
  }, [
    action,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
    variable?.name,
    variableContext.variableIsValid,
    variableDispatch,
  ]);

  const handleCancel = () => {
    let nextPathname = pathname.replace(
      `/${variableName}${isRolloutPage(pathname) ? '/rollout' : ''}/${activeTab}`,
      '',
    );

    nextPathname = `${nextPathname}${isRolloutPage(pathname) ? `/${variableName}/rollout` : ''}`;

    navigate(
      {
        ...location,
        pathname: nextPathname,
      },
      { replace: true },
    );
  };

  if (!isOpen) return null;

  return (
    <Modal
      data-testid="variable-settings-edit-modal"
      className={`variable-settings-modal ${isDisplayNone ? 'transparent' : ''}`}
      maskClosable={false}
      centered
      width={800}
      open={isOpen}
      title={<EditVariableModalTitle onCancel={handleCancel} />}
      closable={false}
      footer={
        <Footer
          activeTab={activeTab}
          onCancel={handleCancel}
          onDeleteAndArchiveClick={() => {
            setIsDisplayNone(true);
          }}
          onDeleteAndArchiveCancel={() => {
            setIsDisplayNone(false);
          }}
          onArchiveOk={() => {
            setIsDisplayNone(false);

            onArchiveOk();
            handleCancel();
          }}
          onDeleteClick={() => {
            setIsDisplayNone(true);
          }}
          onDeleteOk={() => {
            setIsDisplayNone(false);

            onDeleteOk();
            handleCancel();
          }}
          onDeleteCancel={() => {
            setIsDisplayNone(false);
          }}
        />
      }
      styles={{
        body: {
          height: 'calc(100vh - 208px)',
          overflowY: 'auto',
        },
      }}
    >
      <Tabs
        activeKey={activeTab}
        size="small"
        items={[
          {
            key: VariableSettingsAction.EDIT,
            label: <DetailTab />,
            children: (
              <VariableDetail
                onManageEntitiesClick={() => {
                  setIsDisplayNone(true);
                }}
                onManageEntitiesCancel={() => {
                  setIsDisplayNone(false);
                }}
              />
            ),
          },
          {
            key: VariableSettingsAction.TEST,
            label: 'Test',
            children: <GetVariableValue />,
          },
          {
            key: VariableSettingsAction.STATS,
            label: 'Stats',
            children: <Stats />,
          },
          {
            key: VariableSettingsAction.VALIDATE,
            label: <ValidationTab />,
            children: <Validation />,
          },
          ...(isCanaryPilotEnrolled && isCanaryEligible(variable)
            ? [
                {
                  key: VariableSettingsAction.CANARY,
                  label: 'Canary',
                  children: <Canary />,
                },
              ]
            : []),
        ]}
        onChange={(selectedTab: string) => {
          setActiveTab(selectedTab as VariableSettingsAction);

          let nextPathname = pathname.replace(
            `/${variableName}${isRolloutPage(pathname) ? '/rollout' : ''}/${activeTab}`,
            '',
          );

          nextPathname = `${nextPathname}/${variableName}${isRolloutPage(pathname) ? '/rollout' : ''}/${selectedTab}`;

          navigate(
            {
              ...location,
              pathname: nextPathname,
            },
            { replace: true },
          );
        }}
      />
    </Modal>
  );
};

export default EditVariableModal;
